import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const Container = styled.div`
  background: #ffffff;
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2d5a27 0%, #1a3518 100%);
  color: white;
  padding: 48px 16px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  margin: 0;
`;

const Section = styled.section`
  padding: 48px 16px;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 64px 24px;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StepCard = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const StepNumber = styled.div`
  background: #2d5a27;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 600px) {
    margin: 0 auto;
  }
`;

const StepContent = styled.div``;

const StepTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a2e;
`;

const StepDescription = styled.p`
  font-size: 0.85rem;
  line-height: 1.6;
  color: #555;
  margin: 0 0 12px 0;
`;

const StepFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StepFeature = styled.li`
  font-size: 0.8rem;
  color: #666;
  position: relative;
  padding-left: 18px;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #2d5a27;
    font-weight: bold;
  }

  @media (max-width: 600px) {
    padding-left: 0;
    text-align: center;

    &:before {
      position: relative;
      margin-right: 6px;
    }
  }
`;

const BenefitsSection = styled.section`
  background: #f8f9fa;
  padding: 48px 16px;

  @media (min-width: 768px) {
    padding: 64px 24px;
  }
`;

const BenefitsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 32px 0;
  color: #1a1a2e;
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const BenefitCard = styled.div`
  background: #ffffff;
  padding: 24px 20px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  text-align: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const BenefitIcon = styled.div`
  font-size: 1.75rem;
  margin-bottom: 12px;
`;

const BenefitTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a2e;
`;

const BenefitDescription = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: #666;
  margin: 0;
`;

const CTASection = styled.section`
  padding: 48px 16px;
  text-align: center;
  background: white;

  @media (min-width: 768px) {
    padding: 64px 24px;
  }
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: #1a1a2e;
`;

const CTADescription = styled.p`
  font-size: 0.9rem;
  margin: 0 0 24px 0;
  color: #666;
  line-height: 1.5;
`;

const CTAButton = styled.button`
  background: #2d5a27;
  color: #ffffff;
  border: none;
  padding: 12px 32px;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1f3d1b;
    transform: translateY(-1px);
  }
`;

const How = ({ user }) => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <PageTitle>How ShowingBooker Works</PageTitle>
          <PageSubtitle>
            A professional system that transforms how you manage tenant-occupied property showings.
            Automated compliance, documented communications, complete peace of mind.
          </PageSubtitle>
        </HeroContent>
      </HeroSection>

      <Section>
        <StepsContainer>
          <StepCard>
            <StepNumber>1</StepNumber>
            <StepContent>
              <StepTitle>Add Your Properties</StepTitle>
              <StepDescription>
                Set up your properties with tenant information, showing availability, and specific instructions.
                Configure notification preferences and compliance requirements.
              </StepDescription>
              <StepFeatures>
                <StepFeature>Property details management</StepFeature>
                <StepFeature>Tenant contact information</StepFeature>
                <StepFeature>Custom availability windows</StepFeature>
                <StepFeature>Access instructions and codes</StepFeature>
              </StepFeatures>
            </StepContent>
          </StepCard>

          <StepCard>
            <StepNumber>2</StepNumber>
            <StepContent>
              <StepTitle>Schedule Showings</StepTitle>
              <StepDescription>
                Use our intelligent scheduler to book showings. The system automatically checks availability
                and ensures proper advance notice for tenant-occupied units.
              </StepDescription>
              <StepFeatures>
                <StepFeature>Real-time availability</StepFeature>
                <StepFeature>Conflict detection</StepFeature>
                <StepFeature>Calendar integration</StepFeature>
                <StepFeature>Mobile-friendly booking</StepFeature>
              </StepFeatures>
            </StepContent>
          </StepCard>

          <StepCard>
            <StepNumber>3</StepNumber>
            <StepContent>
              <StepTitle>Automatic Compliance</StepTitle>
              <StepDescription>
                ShowingBooker automatically sends legally compliant notices to tenants via multiple channels.
                Every communication is timestamped and stored for your records.
              </StepDescription>
              <StepFeatures>
                <StepFeature>24-hour notice automation</StepFeature>
                <StepFeature>SMS, email, and certified mail</StepFeature>
                <StepFeature>CA Civil Code 1954 compliance</StepFeature>
                <StepFeature>Delivery confirmation tracking</StepFeature>
              </StepFeatures>
            </StepContent>
          </StepCard>

          <StepCard>
            <StepNumber>4</StepNumber>
            <StepContent>
              <StepTitle>Manage & Document</StepTitle>
              <StepDescription>
                Your dashboard provides complete oversight with professional reporting.
                Export compliance reports and maintain audit trails for any disputes.
              </StepDescription>
              <StepFeatures>
                <StepFeature>Centralized dashboard</StepFeature>
                <StepFeature>Complete activity logs</StepFeature>
                <StepFeature>PDF report generation</StepFeature>
                <StepFeature>7-year record retention</StepFeature>
              </StepFeatures>
            </StepContent>
          </StepCard>
        </StepsContainer>
      </Section>

      <BenefitsSection>
        <BenefitsContainer>
          <SectionTitle>Why Professionals Choose ShowingBooker</SectionTitle>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon>⏰</BenefitIcon>
              <BenefitTitle>Save Time</BenefitTitle>
              <BenefitDescription>
                Eliminate phone tag and manual coordination. Automated notifications handle everything.
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon>⚖️</BenefitIcon>
              <BenefitTitle>Legal Protection</BenefitTitle>
              <BenefitDescription>
                Complete compliance documentation protects you from tenant disputes and legal issues.
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon>💼</BenefitIcon>
              <BenefitTitle>Professional Image</BenefitTitle>
              <BenefitDescription>
                Present a polished, tech-forward image that impresses clients and builds trust.
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon>📱</BenefitIcon>
              <BenefitTitle>24/7 Access</BenefitTitle>
              <BenefitDescription>
                Manage showings from anywhere. Mobile-friendly platform works on all devices.
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon>📊</BenefitIcon>
              <BenefitTitle>Clear Documentation</BenefitTitle>
              <BenefitDescription>
                Timestamped records and audit trails for every interaction and notification.
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard>
              <BenefitIcon>🎯</BenefitIcon>
              <BenefitTitle>Focus on Selling</BenefitTitle>
              <BenefitDescription>
                Spend less time on logistics and more time building relationships and closing deals.
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
        </BenefitsContainer>
      </BenefitsSection>

      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Simplify Your Showings?</CTATitle>
          <CTADescription>
            Join property managers and real estate professionals who trust ShowingBooker
            for compliant, efficient, and documented showing management.
          </CTADescription>
          <CTAButton onClick={handleGetStarted}>
            {user ? 'Go to Dashboard' : 'Get Started'}
          </CTAButton>
        </CTAContent>
      </CTASection>

      <Footer />
    </Container>
  );
};

export default How;
